/**
 * Browser auth state machine.
 *
 * Zeron's edge already exposes `POST /auth/exchange` (WorkOS code → tokens) and
 * `POST /auth/refresh` (rotating refresh token → fresh tokens), but the only
 * hosted callback in the product is `/auth/cli/callback`, which prints a paste
 * code for a native engine and sets no browser cookie. A browser session is
 * therefore a new flow, and this module is its state machine.
 *
 * Flow (production — device authorization grant):
 *
 * 1. `beginDeviceLogin()` asks the same-origin BFF to start a WorkOS device
 *    authorization; the BFF keeps the device code in an HttpOnly cookie and
 *    returns the user code plus the AuthKit verification URL.
 * 2. The app shows that URL (and the code). The human approves on AuthKit's own
 *    origin — no password, code, or MFA prompt ever reaches this page.
 * 3. `pollDeviceLogin()` polls the BFF until the grant is approved, at which
 *    point the BFF sets the refresh token in an HttpOnly cookie and returns
 *    only the short-lived access token.
 * 4. `ensureAccessToken()` refreshes ahead of `exp` (60s margin) through
 *    `/api/auth/refresh`, which rotates the cookie. Refreshes are serialized:
 *    WorkOS refresh tokens are single-use, so two concurrent refreshes would
 *    invalidate each other and sign the user out.
 *
 * Why not the redirect flow: it needs a redirect URI registered against the
 * WorkOS environment and the client secret to exchange the code. The device
 * grant needs neither, which is what lets this app be deployed by someone who
 * does not administer the WorkOS environment. The redirect flow is still
 * implemented (`beginLogin`/`completeLogin`) for deployments that do have both.
 *
 * In `AUTH_MODE=dev` (local development only) the BFF returns a fixed user id
 * as the "access token", which the edge's dev auth accepts verbatim. That path
 * exists so the whole app is exercisable without WorkOS credentials; it must
 * never be deployed.
 */

export interface AuthTokens {
  accessToken: string;
  /** Seconds since epoch, decoded from the JWT `exp` claim. */
  expiresAt: number;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export type AuthState =
  | { status: "anonymous" }
  | { status: "authenticating" }
  | { status: "authenticated"; user: AuthUser; tokens: AuthTokens }
  | { status: "expired"; reason: string };

export interface AuthStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export interface AuthDependencies {
  fetch: typeof fetch;
  storage: AuthStorage;
  now: () => number;
  randomBytes: (length: number) => Uint8Array;
  /** Base URL of the BFF. Empty string = same origin. */
  apiBase?: string;
  /** Seconds of slack before `exp` at which a refresh is attempted. */
  refreshMarginSeconds?: number;
}

const PKCE_KEY = "zeron.auth.pkce";
const STATE_KEY = "zeron.auth.state";
const USER_KEY = "zeron.auth.user";
const DEFAULT_REFRESH_MARGIN_SECONDS = 60;

/** RFC 7636 verifier: 43–128 chars from the unreserved alphabet. */
export const createVerifier = (randomBytes: (n: number) => Uint8Array): string => {
  const bytes = randomBytes(32);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return base64UrlEncode(binary);
};

export const base64UrlEncode = (input: string): string =>
  btoa(input).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");

/** S256 challenge. WorkOS AuthKit supports PKCE; without it a public browser
 * client would have to keep the client secret, which it must never do. */
export const createChallenge = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  let binary = "";
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte);
  return base64UrlEncode(binary);
};

/** Decode a JWT payload without verifying it. Verification is the edge's job
 * (WorkOS JWKS); the browser only needs `exp` for refresh scheduling, so an
 * unverified read is safe here — a forged token buys nothing. */
export const decodeJwtClaims = (token: string): Record<string, unknown> | undefined => {
  const parts = token.split(".");
  if (parts.length !== 3) return undefined;
  try {
    return JSON.parse(
      atob((parts[1] as string).replaceAll("-", "+").replaceAll("_", "/"))
    ) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

export const decodeJwtExpiry = (token: string): number | undefined => {
  const claims = decodeJwtClaims(token);
  return typeof claims?.exp === "number" ? claims.exp : undefined;
};

/** The WorkOS `org_id` claim — present when the session was scoped to an org.
 * Workspace/registry routes require it to match the URL's org. */
export const decodeJwtOrgId = (token: string): string | undefined => {
  const claims = decodeJwtClaims(token);
  return typeof claims?.org_id === "string" ? claims.org_id : undefined;
};

/** The WorkOS `sub` claim — the engine's user id. */
export const decodeJwtUserId = (token: string): string | undefined => {
  const claims = decodeJwtClaims(token);
  return typeof claims?.sub === "string" ? claims.sub : undefined;
};

export const memoryStorage = (): AuthStorage => {
  const map = new Map<string, string>();
  return {
    get: (key) => map.get(key) ?? null,
    set: (key, value) => void map.set(key, value),
    remove: (key) => void map.delete(key)
  };
};

export class AuthError extends Error {
  constructor(
    message: string,
    readonly kind: "state_mismatch" | "rejected" | "network" | "config" = "rejected"
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export interface LoginStart {
  url: string;
  state: string;
}

/**
 * A device authorization as the browser sees it.
 *
 * `deviceCode` is deliberately absent: it lives in an HttpOnly cookie, so a
 * script injected into this page cannot use a pending authorization, and a
 * reloaded tab can still finish a login it started.
 */
export interface DeviceAuthorization {
  userCode: string;
  verificationUri: string;
  /** Same page with the code pre-filled — one tap on a tablet. */
  verificationUriComplete: string;
  expiresIn: number;
  interval: number;
}

/**
 * The outcome of one poll. `pending` is the ordinary answer until the human
 * finishes on AuthKit.
 *
 * `interval` is set only when WorkOS answered `slow_down`: it is a request to
 * poll less often, and a client that ignores it will keep being told to slow
 * down.
 */
export type DevicePollResult =
  | { status: "pending"; interval?: number }
  | { status: "approved"; user: AuthUser }
  | { status: "failed"; error: string }
  | { status: "unknown" };

export interface BeginLoginOptions {
  /** WorkOS client id (`WORKOS_CLIENT_ID` on the edge — public, not a secret). */
  clientId: string;
  /** Absolute callback URL registered with WorkOS. */
  redirectUri: string;
  /** WorkOS API base; defaults to the public one. */
  authorizeBase?: string;
  /** Optional organization to scope the session to. */
  organizationId?: string;
}

export class AuthClient {
  private state: AuthState = { status: "anonymous" };
  private inflightRefresh: Promise<AuthTokens | undefined> | undefined;
  /** Observers of auth transitions (the bootstrap uses this to swap the whole
   * screen back to the login card when a session expires mid-flight). */
  private readonly listeners = new Set<(state: AuthState) => void>();
  private readonly marginSeconds: number;
  private readonly apiBase: string;
  /** The redirect URI used by the login currently in flight — the exchange must
   * echo the same one, and reading `window.location` here would break tests. */
  private pendingRedirectUri: string | undefined;

  constructor(private readonly deps: AuthDependencies) {
    this.marginSeconds = deps.refreshMarginSeconds ?? DEFAULT_REFRESH_MARGIN_SECONDS;
    this.apiBase = deps.apiBase ?? "";
  }

  get current(): AuthState {
    return this.state;
  }

  /**
   * Subscribe to auth transitions. Fires immediately with the current state so a
   * subscriber never has to guess where things stand.
   */
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => void this.listeners.delete(listener);
  }

  /** Single funnel for state changes, so no assignment can skip the listeners. */
  private setState(state: AuthState): AuthState {
    this.state = state;
    for (const listener of this.listeners) listener(state);
    return state;
  }

  get user(): AuthUser | undefined {
    return this.state.status === "authenticated" ? this.state.user : undefined;
  }

  /** Access token only if it is still valid past the refresh margin. */
  get accessToken(): string | undefined {
    if (this.state.status !== "authenticated") return undefined;
    const nowSeconds = Math.floor(this.deps.now() / 1000);
    if (this.state.tokens.expiresAt - this.marginSeconds <= nowSeconds) return undefined;
    return this.state.tokens.accessToken;
  }

  /**
   * Build the WorkOS authorize URL and persist the PKCE/state pair. The verifier
   * lives in `sessionStorage` (not `localStorage`) so a second tab cannot
   * complete a login it did not start, and it dies with the tab.
   */
  async beginLogin(options: BeginLoginOptions): Promise<LoginStart> {
    const verifier = createVerifier(this.deps.randomBytes);
    const challenge = await createChallenge(verifier);
    const state = base64UrlEncode(
      [...this.deps.randomBytes(16)].map((b) => String.fromCharCode(b)).join("")
    );
    this.deps.storage.set(PKCE_KEY, verifier);
    this.deps.storage.set(STATE_KEY, state);
    this.pendingRedirectUri = options.redirectUri;
    const base = (options.authorizeBase ?? "https://api.workos.com").replace(/\/+$/, "");
    const params = new URLSearchParams({
      response_type: "code",
      client_id: options.clientId,
      redirect_uri: options.redirectUri,
      provider: "authkit",
      state,
      code_challenge: challenge,
      code_challenge_method: "S256"
    });
    if (options.organizationId) params.set("organization_id", options.organizationId);
    this.setState({ status: "authenticating" });
    return { url: `${base}/user_management/authorize?${params.toString()}`, state };
  }

  /**
   * Validate `state` and hand the code to the BFF. Throws on mismatch — an
   * attacker-supplied `code` must never be exchanged with our verifier.
   */
  async completeLogin(code: string, state: string): Promise<AuthUser> {
    const expected = this.deps.storage.get(STATE_KEY);
    const verifier = this.deps.storage.get(PKCE_KEY);
    this.deps.storage.remove(STATE_KEY);
    this.deps.storage.remove(PKCE_KEY);
    if (expected === null || verifier === null) {
      throw new AuthError("no login in progress in this tab", "state_mismatch");
    }
    if (expected !== state) {
      throw new AuthError("login state mismatch", "state_mismatch");
    }
    const body = await this.post("/api/auth/callback", {
      code,
      codeVerifier: verifier,
      redirectUri: this.pendingRedirectUri ?? ""
    });
    const user = body.user as AuthUser | undefined;
    const accessToken = body.accessToken;
    if (typeof accessToken !== "string" || user === undefined) {
      throw new AuthError("callback response missing tokens", "rejected");
    }
    this.adopt(user, accessToken);
    return user;
  }

  /**
   * Dev-mode login: the BFF mints a fixed identity. Same code path as the real
   * flow so the UI does not branch on auth mode.
   */
  async devLogin(userId: string): Promise<AuthUser> {
    const body = await this.post("/api/auth/dev-login", { userId });
    const user = body.user as AuthUser | undefined;
    const accessToken = body.accessToken;
    if (typeof accessToken !== "string" || user === undefined) {
      throw new AuthError("dev login rejected", "rejected");
    }
    this.adopt(user, accessToken);
    return user;
  }

  /**
   * Begin the device authorization grant (RFC 8628).
   *
   * This is the production login. WorkOS's redirect flow needs a redirect URI
   * registered against the WorkOS environment and the client secret to exchange
   * the code — neither of which a browser client can hold. The device grant
   * needs neither: the human approves on AuthKit's own origin, and the secret
   * never leaves the server.
   */
  async beginDeviceLogin(): Promise<DeviceAuthorization> {
    const body = await this.post("/api/auth/device/start", {});
    const userCode = body.userCode;
    const verificationUri = body.verificationUri;
    if (typeof userCode !== "string" || typeof verificationUri !== "string") {
      throw new AuthError("device authorization response malformed", "rejected");
    }
    this.setState({ status: "authenticating" });
    return {
      userCode,
      verificationUri,
      verificationUriComplete:
        typeof body.verificationUriComplete === "string"
          ? body.verificationUriComplete
          : verificationUri,
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 300,
      interval: typeof body.interval === "number" ? body.interval : 5
    };
  }

  /**
   * Poll once for the outcome of the pending device authorization. The caller
   * owns the loop and its cadence: WorkOS rate-limits the poll, and only the UI
   * knows whether the tab is still visible (a backgrounded tab should back off
   * rather than burn the rate limit).
   */
  async pollDeviceLogin(): Promise<DevicePollResult> {
    const body = await this.post("/api/auth/device/poll", {});
    if (body.status === "pending") {
      return typeof body.interval === "number"
        ? { status: "pending", interval: body.interval }
        : { status: "pending" };
    }
    if (body.status === "failed") {
      const error = typeof body.error === "string" ? body.error : "sign-in failed";
      this.setState({ status: "anonymous" });
      return { status: "failed", error };
    }
    if (body.status === "unknown") return { status: "unknown" };
    const user = body.user as AuthUser | undefined;
    const accessToken = body.accessToken;
    if (typeof accessToken !== "string" || user === undefined) {
      throw new AuthError("device poll response missing tokens", "rejected");
    }
    this.adopt(user, accessToken);
    return { status: "approved", user };
  }

  /**
   * Return a usable access token, refreshing when it is close to expiry.
   * Concurrent callers share one refresh: WorkOS rotates refresh tokens, so two
   * parallel refreshes would race and one would invalidate the other.
   */
  async ensureAccessToken(): Promise<string | undefined> {
    const token = this.accessToken;
    if (token !== undefined) return token;
    if (this.state.status !== "authenticated") return undefined;
    const tokens = await this.refresh();
    return tokens?.accessToken;
  }

  /**
   * Force a refresh (also used by the relay's 401 recovery path). Deliberately
   * not `async`: concurrent callers must receive the *same* promise, and an
   * `async` wrapper would hand each one a fresh one.
   */
  refresh(): Promise<AuthTokens | undefined> {
    if (this.inflightRefresh !== undefined) return this.inflightRefresh;
    const run = this.runRefresh();
    this.inflightRefresh = run;
    void run.finally(() => {
      this.inflightRefresh = undefined;
    });
    return run;
  }

  private async runRefresh(): Promise<AuthTokens | undefined> {
    try {
      const body = await this.post("/api/auth/refresh", {});
      const accessToken = body.accessToken;
      if (typeof accessToken !== "string") {
        throw new AuthError("refresh response missing accessToken", "rejected");
      }
      const user = (body.user as AuthUser | undefined) ?? this.user;
      if (user === undefined) {
        throw new AuthError("refresh response missing user", "rejected");
      }
      return this.adopt(user, accessToken);
    } catch (error) {
      // Only a *rejection* ends the session. WorkOS answers `invalid_grant` for
      // a token that is spent or revoked, and that is the one case where the
      // session really is gone.
      //
      // Anything else — offline, a 5xx, a deploy mid-flight, a refresh race that
      // has already been retried — is transient, and treating it as a logout
      // destroys a working session and makes the user sign in again for nothing.
      // The UI keeps showing the app; the next call retries the refresh.
      const rejected = error instanceof AuthError && error.kind === "rejected";
      if (rejected) {
        this.setState({
          status: "expired",
          reason: error instanceof AuthError ? error.message : String(error)
        });
      }
      return undefined;
    }
  }

  /** Restore a persisted identity so a reload does not flash the login screen.
   * The access token is intentionally not persisted (it lives in memory only);
   * a reload starts by refreshing from the HttpOnly cookie. */
  async restore(): Promise<AuthState> {
    const raw = this.deps.storage.get(USER_KEY);
    if (raw === null) {
      return this.setState({ status: "anonymous" });
    }
    let user: AuthUser;
    try {
      user = JSON.parse(raw) as AuthUser;
    } catch {
      this.deps.storage.remove(USER_KEY);
      return this.setState({ status: "anonymous" });
    }
    this.setState({ status: "authenticated", user, tokens: { accessToken: "", expiresAt: 0 } });
    const tokens = await this.refresh();
    // Forgetting the identity is a *logout*, so it happens only when the session
    // was actually rejected. Dropping it on a transient failure (offline, a 5xx)
    // would force a fresh sign-in for a session that still works.
    if (tokens === undefined && this.state.status === "expired") {
      this.deps.storage.remove(USER_KEY);
    }
    return this.state;
  }

  async signOut(): Promise<void> {
    try {
      await this.post("/api/auth/logout", {});
    } catch {
      // Best effort: the cookie may already be gone.
    }
    this.deps.storage.remove(USER_KEY);
    this.setState({ status: "anonymous" });
  }

  private adopt(user: AuthUser, accessToken: string): AuthTokens {
    const exp = decodeJwtExpiry(accessToken);
    const nowSeconds = Math.floor(this.deps.now() / 1000);
    const tokens: AuthTokens = {
      accessToken,
      // Dev tokens are opaque strings with no exp; treat them as an hour long
      // so the refresh path is still exercised locally.
      expiresAt: exp ?? nowSeconds + 3600
    };
    this.setState({ status: "authenticated", user, tokens });
    this.deps.storage.set(USER_KEY, JSON.stringify(user));
    return tokens;
  }

  private async post(path: string, body: unknown): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await this.deps.fetch(`${this.apiBase}${path}`, {
        method: "POST",
        // The refresh token is an HttpOnly cookie: the browser attaches it, the
        // page never sees it. Same-origin only, so no CORS preflight either.
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body)
      });
    } catch (error) {
      throw new AuthError(`network error: ${String(error)}`, "network");
    }
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const parsed = (await response.json()) as { error?: unknown };
        if (typeof parsed.error === "string") message = parsed.error;
      } catch {
        /* non-JSON error body */
      }
      throw new AuthError(message, response.status >= 500 ? "network" : "rejected");
    }
    try {
      return (await response.json()) as Record<string, unknown>;
    } catch {
      throw new AuthError("malformed response body", "network");
    }
  }
}

/** WebCrypto-backed byte source for the browser. */
export const cryptoRandomBytes = (length: number): Uint8Array => {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
};
